import {
  useFeatureFlags,
  useFeatureFlagsProviderContext,
} from '@brightdrop/feature-flags-client';
import { useEntitlements } from '@gm-commercial/entitlements-client';
import { useMemo } from 'react';
import { EMER_FLAG } from '~/common/models/featureFlags.model';
import { FeatureCodes } from '~/common/models/plans.model';
import { useEmerNotificationsAccess } from '~/routes/settings/hooks/useEmerNotificationsAccess';

/** What the org can open in Safety. */
export type SafetyAccess = {
  /** At least one vehicle has Telematics, which opens every Safety surface. */
  hasTelematicsAccess: boolean;
  /** The EMER flag is on and the org has Protect, alone or in One Fleet. */
  hasEmerAccess: boolean;
  /**
   * The EMER flag is on, so Event history shows EMER events whatever the
   * org's current plan. The backend stores them only for orgs that had
   * Protect, so an org that left Protect keeps its past EMER events.
   */
  isEmerEnabled: boolean;
  /** EMER access without Telematics, which opens only EMER events in Event history. */
  isProtectOnly: boolean;
  /** Feature flag targeting and entitlements have loaded. */
  isReady: boolean;
};

/**
 * Resolves what the org can open in Safety from its vehicles' Telematics
 * enablements, its EMER access, and the EMER flag. Nothing is granted until
 * feature flag targeting and entitlements have loaded, so a Protect-only org
 * is never decided on untargeted flags.
 * @returns Telematics and EMER access, whether Event history shows EMER
 * events, whether the org is Protect-only, and `isReady` once flags and
 * entitlements have loaded.
 */
export const useSafetyAccess = (): SafetyAccess => {
  const { getFlag } = useFeatureFlags();
  const { isUserContextIdentified } = useFeatureFlagsProviderContext();
  const { hasFeatureForAnyAsset, isReady: isEntitlementsReady } =
    useEntitlements();
  const { hasEmerAccess: hasEmerNotificationsAccess } =
    useEmerNotificationsAccess();
  const isReady = isUserContextIdentified && isEntitlementsReady;
  const hasTelematicsAccess = useMemo(
    () => isReady && hasFeatureForAnyAsset(FeatureCodes.TELE, 'vehicle'),
    [hasFeatureForAnyAsset, isReady]
  );
  const hasEmerAccess = isReady && hasEmerNotificationsAccess;

  return {
    hasTelematicsAccess,
    hasEmerAccess,
    isEmerEnabled: isReady && !!getFlag(EMER_FLAG),
    isProtectOnly: hasEmerAccess && !hasTelematicsAccess,
    isReady,
  };
};
